import proj4 from "proj4";
const utm17s = "+proj=utm +zone=17 +south +datum=WGS84 +units=m +no_defs";
const wgs84 = "+proj=longlat +datum=WGS84 +no_defs";

/**
 * Convierte una cadena WKT de tipo POLYGON o MULTIPOLYGON
 * a un objeto GeoJSON (FeatureCollection).
 *
 * @param {string} polygonString - Cadena WKT del polígono.
 * @param {object} [options]
 * @param {boolean} [options.isUTM=true] - Indica si las coordenadas están en UTM y deben transformarse a WGS84.
 * @param {object} [options.properties={}] - Propiedades adicionales que se agregarán al Feature.
 *
 * @returns {GeoJSON.FeatureCollection|null}
 * Retorna un FeatureCollection GeoJSON o null si ocurre un error.
 */
export function polygonToGeoJSON(
    polygonString,
    { isUTM = true, properties = {} } = {},
) {
    try {
        if (!polygonString || typeof polygonString !== "string") {
            throw new Error(
                "El valor de polygonString es inválido o no está definido.",
            );
        }

        let geometryType = "";
        let rawPolygons = [];

        const transformCoord = (coordStr) => {
            const [xStr, yStr] = coordStr.trim().split(/\s+/);
            const x = parseFloat(xStr);
            const y = parseFloat(yStr);

            if (!isFinite(x) || !isFinite(y)) {
                throw new Error(`Coordenada inválida: "${coordStr}"`);
            }

            return isUTM ? proj4(utm17s, wgs84, [x, y]) : [x, y];
        };

        const processRing = (ring) => {
            const coords = ring.map(transformCoord);
            const first = coords[0];
            const last = coords[coords.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
                coords.push(first); // Cierra el anillo si no está cerrado
            }
            return coords;
        };

        if (polygonString.startsWith("POLYGON")) {
            geometryType = "Polygon";
            const cleaned = polygonString
                .replace(/^POLYGON\s*\(\(/, "")
                .replace(/\)\)$/, "");
            const rings = cleaned
                .split("),(")
                .map((ring) => ring.split(",").map((coord) => coord.trim()));
            rawPolygons = [rings];
        } else if (polygonString.startsWith("MULTIPOLYGON")) {
            geometryType = "MultiPolygon";
            const cleaned = polygonString
                .replace(/^MULTIPOLYGON\s*\(\(\(/, "")
                .replace(/\)\)\)$/, "");
            const polygons = cleaned
                .split(")), ((")
                .map((poly) =>
                    poly
                        .split("), (")
                        .map((ring) => ring.split(",").map((coord) => coord.trim())),
                );
            rawPolygons = polygons;
        } else {
            throw new Error("Formato de polígono no reconocido.");
        }

        const geometry =
            geometryType === "Polygon"
                ? {
                    type: "Polygon",
                    coordinates: rawPolygons[0].map(processRing),
                }
                : {
                    type: "MultiPolygon",
                    coordinates: rawPolygons.map((polygon) => polygon.map(processRing)),
                };


        return {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    geometry,
                    properties,
                },
            ],
        };
    } catch (error) {
        console.error(
            "Error al convertir polígono:",
            error.message,
            polygonString,
            properties,
        );
        return null;
    }
}